import React from 'react';
import PropTypes from 'prop-types';

import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import LoadTracker from '../../../../services/LoadTracker';
import TextEntryCorrectResponsesInfo from './TextEntryCorrectResponsesInfo';
import TextEntryStringInput from './TextEntryStringInput';
import TextEntryNumericInput from './TextEntryNumericInput';
import TextEntryMathInput from './TextEntryMathInput';
import TextEntryPrint from './TextEntryPrint';
import PaperBasedInput from '../paperBasedInteraction/PaperBasedInteractionInput';

import questionReader from '../../../../store/readHelpers/QuestionReader';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import sessionReader from '../../../../store/readHelpers/SessionReader';

import styles from './textEntry.scss';
import HiddenItemFlagButton from '../../itemFlag/HiddenItemFlagButton';
import FeedbackNavigationButton from '../../../buttons/FeedbackNavigationButton';
import { t } from '../../../../services/i18nService';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';

const DEFAULT_MAX_LENGTH = 500;

class TextEntryInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired,
        correctResponses: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        services: PropTypes.object,
        userResponse: PropTypes.array.isRequired,
        outcomeIsCorrect: PropTypes.string, // provided by the evaluator as outcomeVariable ONLY if the interaction has been answered correct
        outcomeIsPartiallyCorrect: PropTypes.string,
        skipUnansweredIndicator: PropTypes.bool,
        questionModel: PropTypes.object,
        currentScorableItem: PropTypes.number,
        flaggedItems: PropTypes.array,
        hasValidResponseFeedback: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.mediaTracker = new LoadTracker((success) => {
            props.handleInteractionReady(success);
        }, `Interaction ${this.props.model.identifier}`);
        this.onInputReady = this.mediaTracker.addItemToTrack(`Interaction ${props.model.identifier} input`);
        this.state = {
            expandableInputStyles: undefined,
        };
        this.hiddenFlagNeeded = false;
    }


    componentDidMount() {
        const { model, isReadOnly } = this.props;
        this.mediaTracker.setAllItemsAdded();
        if (!isReadOnly && model.expandAutomatically) {
            this.setExpandableInputDimensions();
            this.expandInputCallback(this.shadowElement.textContent);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const currentUserResponse = this.getUserResponse();
        const nextUserResponse = this.getUserResponse(nextProps.userResponse);

        // useful at question reset
        if (
            !nextProps.isReadOnly
            && nextUserResponse === ''
            && currentUserResponse !== nextUserResponse
        ) {
            if (this.inputComponentInstance.setValue !== undefined) {
                this.inputComponentInstance.setValue(nextUserResponse);
            }
            if (this.props.model.expandAutomatically) {
                this.setState({
                    expandableInputStyles: undefined,
                }, () => {
                    this.setExpandableInputDimensions();
                });
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { correctResponses, isReadOnly, flaggedItems, services, currentScorableItem, hasValidResponseFeedback } = this.props;
        const { expandableInputStyles } = this.state;
        return nextProps.hasValidResponseFeedback !== hasValidResponseFeedback
            || nextProps.correctResponses !== correctResponses
            || nextProps.isReadOnly !== isReadOnly
            || nextProps.flaggedItems !== flaggedItems
            || nextState.expandableInputStyles !== expandableInputStyles
            || (
                services.scorableItemNumberingService.useNumberPerInteraction()
                && nextProps.currentScorableItem !== currentScorableItem
            );
    }

    setExpandableInputDimensions() {
        const computedStyle = window.getComputedStyle(this.inputComponentInstance.inputElement, null);
        const paddingLeft = parseInt(computedStyle.getPropertyValue('padding-left'), 10);
        const paddingRight = parseInt(computedStyle.getPropertyValue('padding-left'), 10);
        const borderWidth = parseInt(computedStyle.getPropertyValue('border-top-width'), 10);
        this.inputPadding = paddingLeft + paddingRight + (borderWidth * 2) + 2;
        this.inputMinimalWidth = parseInt(computedStyle.getPropertyValue('width'), 10);
        this.inputMaximumWidth = this.setMaximumWidth();
    }

    setMaximumWidth() {
        const tdParent = this.inputComponentInstance.inputElement.closest('td');
        if (tdParent) {
            const computedStyle = window.getComputedStyle(tdParent, null);
            return computedStyle.width;
        }
        return '100%';
    }

    /**
     * Get first el from this.props.userResponse array
     * @returns {string}
     */
    getUserResponse = (nextPropsUserResponse) => {
        const userResponseArr = nextPropsUserResponse || this.props.userResponse;
        return Array.isArray(userResponseArr) && userResponseArr.length > 0
            ? userResponseArr[0]
            : '';
    };

    getInputComponent = () => {
        const { model, isReadOnly } = this.props;
        const { baseType, interactionType } = model;

        if (interactionType === 'math') {
            return TextEntryMathInput;
        } else if (isReadOnly) {
            return TextEntryPrint;
        } else if (baseType === 'float') {
            this.hiddenFlagNeeded = true;
            return TextEntryNumericInput;
        }

        this.hiddenFlagNeeded = true;
        return TextEntryStringInput;
    };

    getInputMaxLength = () => {
        const { useInputMaxLength } = this.props.model;
        if (useInputMaxLength) {
            return this.getInputSize();
        }

        return DEFAULT_MAX_LENGTH;
    };

    getInputSize = () => {
        const { expectedLength } = this.props.model;
        return expectedLength > 0 ? expectedLength : null;
    };

    isAttempted = (userResponse) => {
        return userResponse !== undefined && userResponse !== '';
    };

    shouldShowOutcomeIndicator = (props) => {
        const userResponse = this.getUserResponse();
        const isAttempted = this.isAttempted(userResponse);
        return props.correctResponses !== undefined
            && (isAttempted || !props.skipUnansweredIndicator);
    };

    expandInputCallback = (value) => {
        this.shadowElement.textContent = value;
        const totalWidth = this.inputPadding + this.shadowElement.getBoundingClientRect().width;
        this.setState({
            expandableInputStyles: {
                width: `${totalWidth}px`,
                minWidth: `${this.inputMinimalWidth}px`,
                maxWidth: this.inputMaximumWidth,
            },
        });
    };

    render() {
        const {
            outcomeIsCorrect,
            outcomeIsPartiallyCorrect,
            model,
            services,
            skipUnansweredIndicator,
            questionModel,
            callback,
            correctResponses,
            isReadOnly,
            flaggedItems,
            hasValidResponseFeedback,
        } = this.props;
        const { expandableInputStyles } = this.state;
        const userResponse = this.getUserResponse();
        const InputComponent = this.getInputComponent();
        const className = `${styles.textEntryInteraction} interaction ${hasValidResponseFeedback ? styles.buttonWithFeedbackNavigation : ''}`;
        const showCorrectResponsesInfo = correctResponses !== undefined;
        let mathSymbolPalettePreset;
        if (settingsReader.isMathSymbolPalettePresetEnabled()) {
            mathSymbolPalettePreset = questionReader.getMathSymbolPalettePreset(questionModel);
        }
        const interactionNumber = services.scorableItemNumberingService.getInteractionNumber(questionModel.contentRevisionId, model.identifier);
        const isActive = services.scorableItemNumberingService.isActiveScorableItem(interactionNumber);
        const isFlagged = flaggedItems && flaggedItems.includes(`${questionModel.contentRevisionId}-${interactionNumber}`);
        const addHiddenFocusableFlag = this.hiddenFlagNeeded &&
        scorableItemNumberingService.shouldAddHiddenFlag(questionModel.contentRevisionId, model.identifier, null);

        return (
            <span
                className={className}
                style={model.style}
                id={interactionNumber ? `scorableItem-${questionModel.contentRevisionId}_${interactionNumber}` : undefined}
            >
                {
                    model.expandAutomatically ?
                        <span
                            className={styles.shadowElement}
                            ref={(shadowElement) => {
                                this.shadowElement = shadowElement;
                            }}
                            aria-hidden
                        >
                            {userResponse}
                        </span> : null
                }
                {
                    sessionReader.isPrintStylePaperBased()
                        ? <PaperBasedInput
                            questionModel={questionModel}
                            orderNumber={interactionNumber}
                            handleInputReady={this.onInputReady}
                        />
                        : <InputComponent
                            callback={callback}
                            correctResponses={correctResponses}
                            defaultValue={userResponse}
                            handleInteractionReady={this.onInputReady}
                            isReadOnly={isReadOnly}
                            maxLength={this.getInputMaxLength()}
                            model={model}
                            ref={(instance) => {
                                this.inputComponentInstance = instance;
                            }}
                            services={services}
                            size={this.getInputSize()}
                            translationService={services.translationService}
                            mathSymbolPalettePreset={mathSymbolPalettePreset}
                            orderNumber={interactionNumber}
                            questionModel={questionModel}
                            isActive={isActive}
                            isFlagged={isFlagged}
                            onChangeCallback={this.expandInputCallback}
                            expandableInputStyles={expandableInputStyles}
                            keyword={model.keyword}
                        />
                }
                {
                    this.shouldShowOutcomeIndicator(this.props) ?
                        <InteractionOutcomeIndicator
                            addItemToTrack={this.mediaTracker.addItemToTrack}
                            correctResponses={correctResponses}
                            isAttempted={this.isAttempted(userResponse)}
                            outcomeIsCorrect={outcomeIsCorrect}
                            outcomeIsPartiallyCorrect={outcomeIsPartiallyCorrect}
                            skipUnansweredIndicator={skipUnansweredIndicator}
                            translationService={services.translationService}
                        /> : null
                }
                {
                    showCorrectResponsesInfo && <TextEntryCorrectResponsesInfo
                        model={model}
                        outcomeIsCorrect={outcomeIsCorrect}
                        correctResponses={correctResponses}
                    />
                }
                {
                    hasValidResponseFeedback && <FeedbackNavigationButton
                        destinationSelector={`[id="${this.props.model.target}_feedback"]`}
                        label={t('interactions.textEntry.navigateToFeedback')}
                        buttonType="questionEntry"
                    />
                }
                {
                    addHiddenFocusableFlag &&
                        <HiddenItemFlagButton
                            orderNumber={interactionNumber}
                            responseIdentifier={model.identifier}
                            contentRevisionId={questionModel.contentRevisionId}
                        />
                }
            </span>
        );
    }
}

export default TextEntryInteraction;
